import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import {
	assertValidProviderUrl,
	connectorLogoUrlError,
	isGuardingPolicy,
	normalizeAllowedMcpTools,
	normalizeBlockedInternalMcpTools,
	TOOL_ACCESS_LEVELS,
	ToolPolicy,
	withToolAccess,
} from "@productlane/lib/src/browser"

import type { MutatorTx } from "../schema"
import type {
	CreateSidebarSectionArgs,
	DeleteSidebarSectionArgs,
	ReorderSidebarSectionsArgs,
	SetSidebarSectionAccessArgs,
	SetSidebarSectionAllowedToolsArgs,
	SetSidebarSectionBlockedInternalToolsArgs,
	SetSidebarSectionPublicKnowledgeArgs,
	SetSidebarSectionToolAccessArgs,
	SetSidebarSectionToolPolicyArgs,
	SidebarSectionOAuthInput,
	UpdateSidebarSectionArgs,
} from "../types"

export const MAX_GUARDED_TOOLS_PER_SECTION = 40

function assertValidLogoUrl(logoUrl: string | null | undefined) {
	if (!logoUrl) return
	const error = connectorLogoUrlError(logoUrl)
	if (error) throw new Error(error)
}

// The non-secret OAuth columns; the server mutator writes the secret.
function oauthColumns(oauth: SidebarSectionOAuthInput | null) {
	if (!oauth) {
		return {
			oauthTokenUrl: null,
			oauthClientId: null,
			oauthScope: null,
			oauthAudience: null,
		}
	}
	assertValidProviderUrl(oauth.tokenUrl.trim())
	if (!oauth.clientId.trim()) throw new Error("Enter the client ID")
	return {
		oauthTokenUrl: oauth.tokenUrl.trim(),
		oauthClientId: oauth.clientId.trim(),
		oauthScope: oauth.scope?.trim() || null,
		oauthAudience: oauth.audience?.trim() || null,
	}
}

// Client-only optimistic sync of the header rows. The value column is a
// credential that never syncs, so the server mutator override writes the rows
// (with the encrypted value) through Prisma instead.
async function syncHeaders(
	tx: MutatorTx,
	sectionId: string,
	headers: CreateSidebarSectionArgs["headers"],
) {
	if (tx.location !== "client") return

	const existing = await tx.query.sidebarSectionHeaders
		.where("sidebarSectionId", "=", sectionId)
		.run()

	const nextIds = new Set(headers.map((header) => header.id))
	for (const header of existing) {
		if (!nextIds.has(header.id)) {
			await tx.mutate.sidebarSectionHeaders.delete({ id: header.id })
		}
	}

	const existingIds = new Set(existing.map((header) => header.id))
	for (const header of headers) {
		if (existingIds.has(header.id)) {
			await tx.mutate.sidebarSectionHeaders.update({
				id: header.id,
				key: header.key,
				updatedAt: Date.now(),
			})
		} else {
			await tx.mutate.sidebarSectionHeaders.insert({
				id: header.id,
				key: header.key,
				sidebarSectionId: sectionId,
				createdAt: Date.now(),
				updatedAt: Date.now(),
			})
		}
	}
}

export const sidebarSectionMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateSidebarSectionArgs
			ctx: ZeroJwtPayload
		}) => {
			const [existingSidebarSection] = await tx.query.sidebarSections
				.where("id", "=", args.id)
				.run()

			if (existingSidebarSection) {
				throw new Error("Sidebar section already exists")
			}

			assertValidProviderUrl(args.url)
			assertValidLogoUrl(args.logoUrl)

			const allowedMcpTools =
				args.type === "MCP"
					? normalizeAllowedMcpTools(args.allowedMcpTools ?? [])
					: []

			await tx.mutate.sidebarSections.insert({
				id: args.id,
				title: args.title,
				url: args.url,
				logoUrl: args.logoUrl || null,
				description: args.description ?? null,
				order: args.order,
				type: args.type ?? "HTTP",
				// An MCP connector reaches the agent through its tools, so the flag
				// mirrors them instead of being set on its own.
				availableToAgent:
					args.type === "MCP"
						? allowedMcpTools.length > 0
						: args.availableToAgent,
				allowedMcpTools,
				reachableWithoutIdentity: false,
				blockedInternalMcpTools: [],
				...oauthColumns(args.oauth ?? null),
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
			})

			await syncHeaders(tx, args.id, args.headers)
		},
	),
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateSidebarSectionArgs }) => {
			if (args.url !== undefined) assertValidProviderUrl(args.url)
			assertValidLogoUrl(args.logoUrl)

			const [existing] = await tx.query.sidebarSections
				.where("id", "=", args.id)
				.run()
			const flippedType =
				!!args.type && !!existing && existing.type !== args.type

			await tx.mutate.sidebarSections.update({
				id: args.id,
				title: args.title,
				url: args.url,
				...(args.logoUrl !== undefined && { logoUrl: args.logoUrl || null }),
				description: args.description,
				type: args.type,
				// A flip to HTTP has to drop the whitelist, or it stays armed for a
				// later flip back to MCP.
				...(flippedType &&
					args.type === "HTTP" && {
						allowedMcpTools: [],
						blockedInternalMcpTools: [],
						reachableWithoutIdentity: false,
					}),
				// Either flip starts with the agent off: an MCP connector has no tools
				// picked yet, and an HTTP one must be granted the agent on its own.
				...(flippedType && { availableToAgent: false }),
				...(args.oauth !== undefined && oauthColumns(args.oauth)),
				// The agent manifest cache keys on updatedAt, so an edit has to bump it.
				updatedAt: Date.now(),
			})

			await syncHeaders(tx, args.id, args.headers)
		},
	),
	setAllowedTools: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetSidebarSectionAllowedToolsArgs
		}) => {
			const allowedMcpTools = normalizeAllowedMcpTools(args.allowedMcpTools)

			await tx.mutate.sidebarSections.update({
				id: args.id,
				allowedMcpTools,
				// The only place an MCP connector's agent access is decided: one tool
				// switched on is what puts it in front of customers.
				availableToAgent: allowedMcpTools.length > 0,
				updatedAt: Date.now(),
			})
		},
	),
	setBlockedInternalTools: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetSidebarSectionBlockedInternalToolsArgs
		}) => {
			await tx.mutate.sidebarSections.update({
				id: args.id,
				blockedInternalMcpTools: normalizeBlockedInternalMcpTools(
					args.blockedInternalMcpTools,
				),
				updatedAt: Date.now(),
			})
		},
	),
	// Both lists change together, so a tool never passes through a state nobody
	// picked (allowed for customers while still blocked for the team).
	setToolAccess: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetSidebarSectionToolAccessArgs
		}) => {
			if (!TOOL_ACCESS_LEVELS.includes(args.access)) {
				throw new Error("Unknown access level")
			}
			const [section] = await tx.query.sidebarSections
				.where("id", "=", args.id)
				.run()
			if (!section) throw new Error("Connector not found")
			if (section.type !== "MCP") {
				throw new Error("Only an MCP connector has per-tool access")
			}

			const next = withToolAccess(args.tools, args.access, {
				allowedMcpTools: section.allowedMcpTools ?? [],
				blockedInternalMcpTools: section.blockedInternalMcpTools ?? [],
			})
			await tx.mutate.sidebarSections.update({
				id: args.id,
				...next,
				availableToAgent: next.allowedMcpTools.length > 0,
				updatedAt: Date.now(),
			})
		},
	),
	setToolPolicy: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetSidebarSectionToolPolicyArgs
		}) => {
			const [section] = await tx.query.sidebarSections
				.where("id", "=", args.id)
				.run()
			if (!section) throw new Error("Connector not found")
			if (!args.tool.trim()) throw new Error("Tool name is required")

			const policies = { ...(section.toolPolicies ?? {}) }

			// A no-op policy is stored as no policy, so the tool reads as unguarded
			// everywhere instead of carrying an empty guard.
			if (!args.policy || !isGuardingPolicy(args.policy)) {
				delete policies[args.tool]
			} else {
				// Re-validated here because the client owns none of this shape.
				policies[args.tool] = ToolPolicy.parse(args.policy)
			}

			if (Object.keys(policies).length > MAX_GUARDED_TOOLS_PER_SECTION) {
				throw new Error(
					`A connector can guard at most ${MAX_GUARDED_TOOLS_PER_SECTION} tools`,
				)
			}

			await tx.mutate.sidebarSections.update({
				id: args.id,
				toolPolicies: Object.keys(policies).length > 0 ? policies : null,
				// The tool listing and manifest caches key on updatedAt, so a policy
				// edit has to bump it or redaction lags enforcement.
				updatedAt: Date.now(),
			})
		},
	),
	setAccess: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetSidebarSectionAccessArgs
		}) => {
			const [section] = await tx.query.sidebarSections
				.where("id", "=", args.id)
				.run()
			// An MCP connector's access follows its per-tool switches, so a direct
			// write here would leave the tag saying something the agent cannot do.
			if (section?.type === "MCP") {
				throw new Error(
					"An MCP connector's agent access follows its tool switches",
				)
			}

			await tx.mutate.sidebarSections.update({
				id: args.id,
				availableToAgent: args.availableToAgent,
				updatedAt: Date.now(),
			})
		},
	),
	setPublicKnowledge: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: SetSidebarSectionPublicKnowledgeArgs
		}) => {
			const [section] = await tx.query.sidebarSections
				.where("id", "=", args.id)
				.run()
			// An HTTP connector always resolves one customer's account, so it can
			// never answer a visitor who has not proven their email.
			if (section?.type === "HTTP") {
				throw new Error("An HTTP connector always needs a verified customer")
			}

			await tx.mutate.sidebarSections.update({
				id: args.id,
				reachableWithoutIdentity: args.reachableWithoutIdentity,
				updatedAt: Date.now(),
			})
		},
	),
	reorder: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: ReorderSidebarSectionsArgs
		}) => {
			for (let i = 0; i < args.sectionIds.length; i++) {
				await tx.mutate.sidebarSections.update({
					id: args.sectionIds[i]!,
					order: i,
				})
			}
		},
	),
	delete: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: DeleteSidebarSectionArgs }) => {
			// Hard delete - remove from database
			await tx.mutate.sidebarSections.delete({ id: args.id })
		},
	),
}
